import { FC } from "react";
import "./SourceLinks.scss";

export interface Source {
    /** Display text for the source link */
    readonly title: string;
    /** URL to open when clicked */
    readonly url: string;
    /** Whether to open in new tab (default: true) */
    readonly newTab?: boolean;
}

export interface SourceLinksProps {
    /** Array of sources to display */
    readonly sources: readonly Source[];
    /** Optional prefix text before the links (default: "Sources:") */
    readonly prefix?: string;
    /** Optional callback when a source is clicked */
    onSourceClick?: (source: Source) => void;
}

/**
 * Displays source links below a chat message.
 * Designed to be compact and unobtrusive, unlike suggestion chips.
 */
const SourceLinks: FC<SourceLinksProps> = (props) => {
    const { sources, prefix = "Sources:", onSourceClick } = props;

    if (!sources || sources.length === 0) {
        return null;
    }

    const handleClick = (source: Source, e: React.MouseEvent) => {
        if (onSourceClick) {
            e.preventDefault();
            onSourceClick(source);
        }
    };

    return (
        <div className="source-links">
            {prefix && <span className="source-links__prefix">{prefix}</span>}
            <span className="source-links__list">
                {sources.map((source, index) => (
                    <span key={index} className="source-links__item">
                        <a
                            href={source.url}
                            target={source.newTab !== false ? "_blank" : "_self"}
                            rel={source.newTab !== false ? "noopener noreferrer" : undefined}
                            className="source-links__link"
                            onClick={(e) => handleClick(source, e)}
                            title={source.url}
                        >
                            {source.title}
                        </a>
                        {index < sources.length - 1 && (
                            <span className="source-links__separator">&middot;</span>
                        )}
                    </span>
                ))}
            </span>
        </div>
    );
};

export default SourceLinks;
